import React, { useState, useEffect } from 'react';
import { Calendar, Star, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { buildWhatsAppUrl, trackWhatsAppClick } from '@/lib/whatsapp';
import { trackBookTrialLesson } from '@/lib/analytics';

const WHATSAPP_LINK = buildWhatsAppUrl({ variant: 'default' });

// Subject rotation - Updated to reflect full subject coverage
const SUBJECTS = [
  'VCE',
  'Selective Entry',
  'IELTS',
  'PTE',
  'GMAT',
  'GRE',
  'SAT',
  'English',
  'Mathematics',
  'Chemistry',
  'Biology',
  'Physics',
  'Essay Writing',
  'Writing',
  'Spanish',
  'Coding',
  'AI'
];

const HeroSection = ({ scrollToBooking }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);

  useEffect(() => {
    const currentSubject = SUBJECTS[currentIndex];
    
    const handleTyping = () => {
      if (!isDeleting) {
        // Typing forward
        if (displayText.length < currentSubject.length) {
          setDisplayText(currentSubject.substring(0, displayText.length + 1));
          setTypingSpeed(120);
        } else {
          // Finished typing, pause then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
          return;
        }
      } else {
        // Deleting backward
        if (displayText.length > 0) {
          setDisplayText(displayText.substring(0, displayText.length - 1));
          setTypingSpeed(80);
        } else {
          // Finished deleting, move to next subject
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % SUBJECTS.length);
          setTypingSpeed(500);
        }
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayText, isDeleting, currentIndex, typingSpeed]);

  return (
    <section className="hero-section">
      <div className="hero-overlay"></div>
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center relative z-10">
        <div className="space-y-8">
          <div className="accent-bar-left"></div>
          <h1 className="heading-xl">
            Expert Tutoring for{' '}
            <span 
              style={{ 
                display: 'inline-block',
                background: '#B7BF72',
                color: '#000000',
                fontWeight: '700',
                minWidth: '200px',
                textAlign: 'left',
                padding: '4px 10px',
                borderRadius: '8px',
                verticalAlign: 'middle'
              }}
            >
              {displayText}
            </span>{' '}
            & more
          </h1>
          <p className="body-lg" style={{ color: 'var(--text-grey)' }}>
            Premium online tutoring available worldwide. Personalized strategies, detailed feedback, proven results, and comprehensive course materials for test prep and academic success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Button onClick={() => { trackBookTrialLesson('hero'); scrollToBooking(); }} className="btn-primary">
              <Calendar className="w-5 h-5" />
              Book Free Trial Session
            </Button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('hero', 'default')}
              data-testid="hero-whatsapp-btn"
            >
              <Button className="btn-teal">
                <MessageCircle className="w-5 h-5" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>
          <div className="flex items-center gap-8 pt-6">
            <div className="flex items-center gap-3">
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${i}`} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <span className="text-sm font-semibold" style={{ color: 'var(--text-charcoal)' }}>5.0 Rating</span>
            </div>
            <div className="text-sm font-medium" style={{ color: 'var(--text-grey)' }}>
              100+ Students Helped
            </div>
          </div>
        </div>
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b"
            alt="Online tutoring session"
            className="rounded-2xl shadow-2xl w-full h-auto object-cover"
            style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}
          />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
